import classNames from "classnames";
import * as React from "react";
import { RenderPanel } from "source/app/presentation/Shared/ActivePanel/Render/RenderPanel";
import { RouteUrl } from "source/app/presentation/Shared/ActivePanel/Routing/RouteUrl";
import { WithRouterParams } from "source/app/presentation/Shared/ActivePanel/Routing/WithRouterParams";
import { EEventType } from "source/app/services/Events/EEventType";
import {
    DispatchEvent,
    SubscribeToEvent,
} from "source/app/services/Events/Events";
import { EPanelId } from "source/app/services/Shared/EPanelId";
import { State } from "source/app/services/UI/State/State";
import { Emotions } from "../../../domain/Emotions/Emotions";
import Player from "../../../domain/Player";
import { ESharedComponent } from "../../../services/UI/State/ESharedComponent";
import { GetWorkflowsState } from "../../MainPanel/Workflows/Workflows";
import {
    NavigateTo,
    RouterNavigationTarget,
} from "../../Routing/RouterNavigationTarget";
import Render from "../Render";
import { Style, StyleClass } from "../Styles/CSS";
import * as styles from "./ActivePanel.css";
import { DisplayBroadStateSelectors } from "./BroadStateSelectors/BroadStateSelectors";
import { IActivePanelProps } from "./IActivePanelProps";
import { IActivePanelState } from "./IActivePanelState";
import { NavigationMenu } from "./NavigationMenu/NavigationMenu";
import { GetNavigationMenuItems } from "./NavigationMenu/NavigationMenuItems";
import { RouteWorkflowsAsync } from "./Routing/RouteWorkflows";
import { Title } from "./Title/Title";
import { IRoutingInfo } from "./Routing/IRoutingInfo";

export class ActivePanel extends React.PureComponent<
    IActivePanelProps,
    IActivePanelState
> {
    private panelStackRef = React.createRef<HTMLDivElement>();
    private panelTransitionFrameId?: number;
    private panelTransitionTimeoutId?: number;

    constructor(props: IActivePanelProps) {
        super(props);

        this.state = {
            // params: this.props.params,
            activePanelId: defaultActivePanelId,
            activePanelName: EPanelId[defaultActivePanelId],
            aheadByMinutes: 0,
            currentEmotion: Emotions.Default,
            transitionSourcePanelId: undefined,
            isPanelTransitionReady: false,
            panelStackHeight: undefined,
        };
    }

    async componentDidMount() {
        SubscribeToEvent(EEventType.Workflows_Mounted, () =>
            OnWorkflowsMounted(this.props)
        );

        NavigateTo(window.location.pathname);
        State.ShareClass(ESharedComponent.ActivePanel, this);
    }

    componentWillUnmount() {
        this.clearPanelTransitionTimers();
    }

    async componentDidUpdate(prevProps: IActivePanelProps) {
        if (prevProps.params !== this.props.params) {
            RouteWorkflowsAsync(this.props.params);
        }

        // this.setState({ params: this.props.params });
        RouteUrl(this);
        State.ShareClass(ESharedComponent.ActivePanel, this);
    }

    public TransitionToPanel(nextPanelId: EPanelId) {
        const previousPanelId = this.state.activePanelId;

        if (previousPanelId === nextPanelId) {
            return;
        }

        this.clearPanelTransitionTimers();

        const panelStackHeight = this.getPanelStackHeight();

        this.setState({
            activePanelId: nextPanelId,
            activePanelName: EPanelId[nextPanelId],
            transitionSourcePanelId: previousPanelId,
            isPanelTransitionReady: false,
            panelStackHeight,
        });

        this.panelTransitionFrameId = window.requestAnimationFrame(() => {
            this.panelTransitionFrameId = window.requestAnimationFrame(() => {
                this.setState({ isPanelTransitionReady: true });

                this.panelTransitionTimeoutId = window.setTimeout(() => {
                    this.setState({
                        transitionSourcePanelId: undefined,
                        isPanelTransitionReady: false,
                        panelStackHeight: undefined,
                    });
                }, panelTransitionDurationMs);
            });
        });
    }

    private getPanelStackHeight() {
        const panelStack = this.panelStackRef.current;

        if (panelStack === null) {
            return this.state.panelStackHeight;
        }

        return panelStack.getBoundingClientRect().height;
    }

    private clearPanelTransitionTimers() {
        if (this.panelTransitionFrameId !== undefined) {
            window.cancelAnimationFrame(this.panelTransitionFrameId);
            this.panelTransitionFrameId = undefined;
        }

        if (this.panelTransitionTimeoutId !== undefined) {
            window.clearTimeout(this.panelTransitionTimeoutId);
            this.panelTransitionTimeoutId = undefined;
        }
    }

    render(): JSX.Element | null {
        if (this.state.activePanelId === undefined) {
            return null;
        }

        const shouldDisplay = Emotions.GetFromCharacter(
            this.state.currentEmotion.character
        ).IsAtLeast(Emotions.Complacent);

        return (
            <Render id="activePanel" when={Player.IsSignedIn && shouldDisplay}>
                <div
                    id="pnlMain"
                    className={classNames(
                        styles.container,
                        "pnlMainContainer mainPanelSections col-xs-12",
                        StyleClass(`pnlMainContainer_layout`, {
                            display: `flex`,
                            flexDirection: `column`,
                            minHeight: `100vh`,
                        })
                    )}
                >
                    <NavigationMenu
                        activePanelTitle={this.state.activePanelId}
                    />
                    <Title
                        id={this.state.activePanelId}
                        withClass={styles.title}
                    />
                    <RenderAllPanels
                        {...{
                            routingParams: this.props.params,
                            activePanelId: this.state.activePanelId,
                            transitionSourcePanelId:
                                this.state.transitionSourcePanelId,
                            isPanelTransitionReady:
                                this.state.isPanelTransitionReady,
                            panelStackHeight: this.state.panelStackHeight,
                            panelStackRef: this.panelStackRef,
                        }}
                    />
                    <div
                        id="additionalPanels"
                        {...Style(`additionalPanels`, {
                            clear: `both`,
                            width: `100%`,
                            marginTop: `auto`,
                            marginBottom: `15px`,
                        })}
                    >
                        <DisplayBroadStateSelectors />
                    </div>
                </div>
            </Render>
        );
    }
}

export const defaultActivePanelId = EPanelId.Habits;

export default WithRouterParams(ActivePanel);

function RenderAllPanels({
    // hasElementIdChanged,
    activePanelId,
    transitionSourcePanelId,
    isPanelTransitionReady,
    panelStackHeight,
    panelStackRef,
}: {
    // hasElementIdChanged: boolean;
    activePanelId: EPanelId;
    transitionSourcePanelId?: EPanelId;
    isPanelTransitionReady: boolean;
    panelStackHeight?: number;
    panelStackRef: React.RefObject<HTMLDivElement>;
}) {
    return (
        <div
            ref={panelStackRef}
            className={styles.panelStack}
            style={
                panelStackHeight !== undefined
                    ? { height: `${panelStackHeight}px` }
                    : undefined
            }
        >
            {GetNavigationMenuItems().map((navigationLink, index) => {
                const isActive = navigationLink.id === activePanelId;

                return (
                    <RenderPanel
                        key={index}
                        navigationMenuItem={navigationLink}
                        // hasElementIdChanged={hasElementIdChanged}
                        isActive={isActive}
                        transitionSourcePanelId={transitionSourcePanelId}
                        isPanelTransitionReady={isPanelTransitionReady}
                    />
                );
            })}
        </div>
    );
}

const panelTransitionDurationMs = 240;

function OnWorkflowsMounted(props: IActivePanelProps) {
    RouteWorkflowsAsync({
        ...props.params,
    });

    // Prevent bug with Workflows nav menu item link not working
    // when the application  is loaded
    const isDefaultUrl = JSON.stringify(props.params) === "{}";

    if (isDefaultUrl) {
        NavigateTo(`/${EPanelId[defaultActivePanelId]}`);
    }
}
